import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MagnifyingGlassIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { HelpLayout } from "./components/HelpLayout";
import { faqEntries } from "./content/faq";
import { FAQ_CATEGORIES } from "../../lib/help/types";
import { getRelatedGuidesForFaq } from "../../lib/help/related";

export function FaqPage() {
  const location = useLocation();
  const [filter, setFilter] = useState("");

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return faqEntries;
    return faqEntries.filter(
      (e) =>
        e.question.toLowerCase().includes(q) ||
        e.answer.toLowerCase().includes(q) ||
        e.tags.some((t) => t.includes(q)),
    );
  }, [filter]);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        (el as HTMLDetailsElement).open = true;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  return (
    <HelpLayout crumbs={[{ label: "FAQ" }]}>
      <header className="help-heading mb-12">
        <h1 className="font-display text-4xl font-bold text-bark-900">Frequently asked questions</h1>
        <p className="mt-2 max-w-2xl text-lg text-bark-700">
          Quick answers for schools evaluating, buying, installing, or running BookBurrow.
        </p>
        <label className="relative mt-6 block max-w-xl">
          <span className="sr-only">Filter FAQs</span>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-bark-600" aria-hidden="true" />
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter questions…"
            className="paper-card h-11 w-full rounded-full border border-cream-300 bg-cream-50 pl-11 pr-4 text-base text-bark-900 shadow-sm placeholder:text-bark-600/70 focus:border-forest-500 focus:outline-none focus:ring-2 focus:ring-forest-300"
          />
        </label>
      </header>

      <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
        <aside className="hidden lg:block">
          <nav className="sticky top-24">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-bark-600">Jump to</p>
            <ul className="space-y-2">
              {FAQ_CATEGORIES.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={`#cat-${cat.id}`}
                    className="block rounded-lg px-3 py-1.5 text-sm text-bark-700 hover:bg-cream-100 hover:text-forest-700"
                  >
                    {cat.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="space-y-12">
          {FAQ_CATEGORIES.map((cat) => {
            const items = filtered.filter((e) => e.category === cat.id);
            if (!items.length) return null;
            return (
              <section key={cat.id} id={`cat-${cat.id}`} className="scroll-mt-24">
                <h2 className="font-display text-2xl font-semibold text-bark-900">{cat.title}</h2>
                <div className="paper-card mt-4 divide-y divide-cream-300 overflow-hidden rounded-2xl border border-cream-300 bg-cream-50">
                  {items.map((entry) => {
                    const relatedGuides = getRelatedGuidesForFaq(entry, 3);
                    return (
                      <details
                        key={entry.id}
                        id={entry.id}
                        className="group scroll-mt-24 px-5 py-4 open:bg-cream-100/60"
                      >
                        <summary className="flex cursor-pointer items-start justify-between gap-4 text-left list-none">
                          <span className="font-display text-lg font-semibold text-bark-900">
                            {entry.question}
                          </span>
                          <ChevronDownIcon
                            className="mt-1 h-5 w-5 flex-shrink-0 text-forest-600 transition-transform group-open:rotate-180"
                            aria-hidden="true"
                          />
                        </summary>
                        <p className="mt-3 leading-relaxed text-bark-700">{entry.answer}</p>
                        {relatedGuides.length > 0 && (
                          <p className="mt-3 text-sm text-bark-600">
                            See also:{" "}
                            {relatedGuides.map((guide, i) => (
                              <span key={guide.frontmatter.slug}>
                                <Link
                                  to={`/help/guides/${guide.frontmatter.slug}`}
                                  className="text-forest-700 underline decoration-gold-400 decoration-2 underline-offset-4 hover:text-forest-800"
                                >
                                  {guide.frontmatter.title}
                                </Link>
                                {i < relatedGuides.length - 1 ? ", " : ""}
                              </span>
                            ))}
                          </p>
                        )}
                      </details>
                    );
                  })}
                </div>
              </section>
            );
          })}
          {!filtered.length && (
            <p className="paper-card rounded-2xl border border-dashed border-cream-300 bg-cream-50 p-8 text-center text-bark-700">
              No questions match "{filter}". Try different keywords, or{" "}
              <a href="mailto:support@bookburrow.uk" className="text-forest-700 underline">
                email support
              </a>
              .
            </p>
          )}
        </div>
      </div>
    </HelpLayout>
  );
}
